"use client";

import { useEffect, useRef } from "react";

const DEFAULT_MIN_HIDDEN_MS = 60_000;

/**
 * Fires `onResume` when the page comes back after a suspension:
 *
 *  - `pageshow` with `event.persisted` — the page was restored from the
 *    back/forward cache (iOS Safari back-swipe, tab restore), meaning no
 *    mount effects re-ran and any in-memory state may be arbitrarily old.
 *  - `visibilitychange` to visible after being hidden for at least
 *    `minHiddenMs` — the iOS app-switch case, where Safari suspends the tab
 *    without evicting it.
 *
 * Callers use this to invalidate resume-sensitive state (e.g. a cached
 * Turnstile token); keep the callback safe to fire spuriously.
 */
export function usePageResume(
  onResume: () => void,
  { minHiddenMs = DEFAULT_MIN_HIDDEN_MS }: { minHiddenMs?: number } = {},
): void {
  // Ref so listener identity is stable even if the caller passes a fresh
  // closure each render; updated in an effect per react-hooks/refs.
  const onResumeRef = useRef(onResume);
  useEffect(() => {
    onResumeRef.current = onResume;
  }, [onResume]);

  useEffect(() => {
    let hiddenAt: number | null = null;

    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) onResumeRef.current();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        hiddenAt = Date.now();
        return;
      }
      if (hiddenAt !== null && Date.now() - hiddenAt >= minHiddenMs) {
        onResumeRef.current();
      }
      hiddenAt = null;
    };

    window.addEventListener("pageshow", handlePageShow);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("pageshow", handlePageShow);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [minHiddenMs]);
}
